export const BACKTEST_STATUS_LABELS = {
  queued: 'Queued',
  building: 'Building',
  deploying: 'Deploying',
  waiting_for_capacity: 'Waiting for Capacity',
  pulling: 'Pulling Image',
  starting: 'Starting',
  booting: 'Booting',
  running: 'Running',
  backtesting: 'Backtesting',
  completed: 'Completed',
  completed_no_trades: 'Completed – No Trades',
  completed_with_errors: 'Completed with Errors',
  failed: 'Failed',
  force_stopped: 'Stopped',
  terminated_timeout: 'Stopped (Timed Out)',
  stopped: 'Stopped',
  not_found: 'Not Found',
  initiated: 'Initiated',
  initiating: 'Initiating',
  in_progress: 'In Progress'
};

export const COMPLETED_STATES = ['completed', 'completed_no_trades', 'completed_with_errors'];

export const TERMINAL_STATUSES = [
  ...COMPLETED_STATES,
  'failed',
  'force_stopped',
  'terminated_timeout',
  'stopped',
  'not_found'
];

export const NO_TRADES_FALLBACK_MESSAGE =
  'Backtest finished without executing any trades. Review your entry conditions or date range to allow entries.';

export const cleanStatusKey = (value = '') =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const finiteNumberOrNull = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const getNestedNumber = (source, keys) => {
  if (!source || typeof source !== 'object') return null;
  for (const key of keys) {
    const value = finiteNumberOrNull(source[key]);
    if (value !== null) return value;
  }
  return null;
};

export const hasNoTradesSignal = (backtest) => {
  if (!backtest || typeof backtest !== 'object') return false;
  const statusKey = cleanStatusKey(backtest.status);
  if (statusKey === 'completed_no_trades') return true;
  if (statusKey !== 'completed') return false;

  const explicitTradeCount = getNestedNumber(backtest, [
    'tradeCount',
    'tradesCount',
    'totalTrades',
    'filledTrades',
    'filledTradeCount',
    'numberOfTrades',
  ]);
  if (explicitTradeCount === 0) return true;

  const metricsTradeCount = getNestedNumber(backtest.metrics, [
    'tradeCount',
    'tradesCount',
    'totalTrades',
    'filledTrades',
    'filledTradeCount',
    'numberOfTrades',
  ]);
  if (metricsTradeCount === 0) return true;

  const summaryTradeCount = getNestedNumber(backtest.summary, [
    'tradeCount',
    'tradesCount',
    'totalTrades',
    'filledTrades',
    'filledTradeCount',
    'numberOfTrades',
  ]);
  if (summaryTradeCount === 0) return true;

  return Object.prototype.hasOwnProperty.call(backtest, 'trades') &&
    Array.isArray(backtest.trades) &&
    backtest.trades.length === 0;
};

export const normalizeBacktestStatus = (backtest) => {
  const statusKey = cleanStatusKey(backtest?.status);
  if (hasNoTradesSignal(backtest)) return 'completed_no_trades';
  return statusKey || backtest?.status || '';
};

export const normalizeBacktestRecord = (backtest) => {
  if (!backtest || typeof backtest !== 'object') return backtest;
  return {
    ...backtest,
    status: normalizeBacktestStatus(backtest),
  };
};

export const isCompletedStatus = (status) => COMPLETED_STATES.includes(cleanStatusKey(status));

export const isTerminalStatus = (status) => TERMINAL_STATUSES.includes(cleanStatusKey(status));

export const normalizeStatusLabel = (status, stage, isRunning = false) => {
  const statusKey = cleanStatusKey(stage) || cleanStatusKey(status);
  if (!statusKey) {
    return isRunning ? 'Running' : 'Unknown';
  }
  return BACKTEST_STATUS_LABELS[statusKey] || (isRunning ? 'Running' : statusKey);
};

export const getStatusDescription = (statusKey, description) => {
  if (description) return description;
  if (cleanStatusKey(statusKey) === 'completed_no_trades') {
    return NO_TRADES_FALLBACK_MESSAGE;
  }
  return '';
};

export async function finalizeCompletedBacktest({
  statusKey,
  displayStatus,
  progressInfo,
  description,
  isNoTrades,
  stopPolling,
  stopTimer,
  setIsLoading,
  fetchBacktestHistory,
  setTableRefreshKey,
  setBacktestStatus,
  setError,
  onAnalytics,
  analyticsPayload = {}
}) {
  stopPolling?.();
  stopTimer?.();
  setIsLoading?.(false);
  setError?.('');

  const normalizedDescription = getStatusDescription(statusKey, description);

  setBacktestStatus?.({
    status: displayStatus,
    statusCode: cleanStatusKey(statusKey),
    progress: progressInfo || null,
    description: normalizedDescription,
    isConnectionError: false,
    running: false // Backtest is complete, not running
  });

  if (fetchBacktestHistory) {
    try {
      await fetchBacktestHistory();
    } catch (err) {
      console.error('[finalizeCompletedBacktest] Failed to refresh history', err);
    }
  }

  setTableRefreshKey?.((prev) => (typeof prev === 'number' ? prev + 1 : 1));

  onAnalytics?.({
    ...analyticsPayload,
    status: isNoTrades ? 'completed_no_trades' : 'completed',
    noTrades: Boolean(isNoTrades)
  });
}
